import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Translate, translateDateFormat } from '../../../../../shared/LanguageService';
import { FirmProduct, PropertyAddress } from '../types';

type BookingSummaryProps = {
    isBookableInCalendar: boolean;
    isSmallerScreen: boolean;
    photographerName: string | null;
    bookingDate: Date;
    timeslot: string | null;
    address: PropertyAddress;
    chosenProducts: FirmProduct[];
    total: { amount: number; currencySign: string } | null;
};

const card = { padding: '16px', display: 'grid', gap: '12px', fontSize: '13px' };

function Fact({ label, value }: { label: string; value: string }) {
    return (
        <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{label}</Typography>
            <Typography sx={{ fontSize: '13px' }}>{value}</Typography>
        </Box>
    );
}

export function BookingSummary({
    isBookableInCalendar,
    isSmallerScreen,
    photographerName,
    bookingDate,
    timeslot,
    address,
    chosenProducts,
    total
}: BookingSummaryProps) {
    let notChosen = Translate('order/create/v2/summary/not-chosen');

    return (
        <Box
            component="section"
            aria-label={Translate('order/create/v2/step/photographer/summary')}
            sx={{ display: 'grid', gap: '16px' }}
        >
            {isBookableInCalendar ? (
                <Alert severity="info" variant="outlined" sx={{ fontSize: '14px' }}>
                    {Translate(
                        isSmallerScreen
                            ? 'order/create/v2/step/photographer/hint-small-screen'
                            : 'order/create/v2/step/photographer/hint'
                    )}
                </Alert>
            ) : (
                <Alert severity="info" sx={{ fontSize: '14px' }}>
                    {Translate('order/create/v2/step/photographer/not-bookable')}
                </Alert>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: '8px'
                }}
            >
                {isBookableInCalendar && (
                    <Paper variant="outlined" sx={card}>
                        <Fact
                            label={Translate('order/create/v2/summary/photographer')}
                            value={photographerName || notChosen}
                        />
                        <Fact
                            label={Translate('order/create/v2/summary/date')}
                            value={translateDateFormat(bookingDate, 'd MMMM yyyy')}
                        />
                        <Fact
                            label={Translate('order/create/v2/summary/time')}
                            value={timeslot || notChosen}
                        />
                    </Paper>
                )}
                <Paper variant="outlined" sx={card}>
                    <Fact
                        label={Translate('order/create/v2/summary/address')}
                        value={address.addressLine1}
                    />
                    <Fact
                        label={Translate('order/create/v2/summary/postal-number')}
                        value={address.addressPostalNumber}
                    />
                    <Fact
                        label={Translate('order/create/v2/summary/city')}
                        value={address.addressCity}
                    />
                </Paper>
            </Box>
            <Paper variant="outlined" sx={card}>
                {chosenProducts.map((product) => (
                    <Box key={product.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{product.name}</span>
                        {product.priceBrokerFirm && (
                            <span>{`${product.priceBrokerFirm.value}${product.currencySign}`}</span>
                        )}
                    </Box>
                ))}
                {total && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            borderTop: '1px solid',
                            paddingTop: '8px',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}
                    >
                        <span>{Translate('order/create/v2/total')}</span>
                        <span>{`${total.amount}${total.currencySign}`}</span>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
